export interface InputFrame {
  moveX: number;
  moveY: number;
  sprint: boolean;
  kickPressed: boolean;
  pausePressed: boolean;
  cameraX: number;
  cameraY: number;
}

export class InputSystem {
  private readonly keys = new Set<string>();
  private kickQueued = false;
  private pauseQueued = false;
  private joystickX = 0;
  private joystickY = 0;
  private cameraX = 0;
  private cameraY = 0;
  private previousGamepadButtons: boolean[] = [];
  private joystickPointer: number | null = null;

  constructor() {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.clear);
  }

  bindTouchControls(joystick: HTMLElement, knob: HTMLElement, kick: HTMLElement, pause: HTMLElement): void {
    const move = (event: PointerEvent) => {
      if (this.joystickPointer !== event.pointerId) return;
      const rect = joystick.getBoundingClientRect();
      const dx = event.clientX - (rect.left + rect.width / 2);
      const dy = event.clientY - (rect.top + rect.height / 2);
      const radius = rect.width * 0.34;
      const length = Math.hypot(dx, dy);
      const scale = length > radius ? radius / length : 1;
      this.joystickX = dx * scale / radius;
      this.joystickY = -dy * scale / radius;
      knob.style.transform = `translate(${dx * scale}px, ${dy * scale}px)`;
    };
    const release = (event: PointerEvent) => {
      if (this.joystickPointer !== event.pointerId) return;
      this.joystickPointer = null;
      this.joystickX = this.joystickY = 0;
      knob.style.transform = 'translate(0, 0)';
    };
    joystick.addEventListener('pointerdown', (event) => {
      this.joystickPointer = event.pointerId;
      joystick.setPointerCapture(event.pointerId);
      move(event);
    });
    joystick.addEventListener('pointermove', move);
    joystick.addEventListener('pointerup', release);
    joystick.addEventListener('pointercancel', release);
    kick.addEventListener('pointerdown', (event) => { event.preventDefault(); this.kickQueued = true; });
    pause.addEventListener('pointerdown', (event) => { event.preventDefault(); this.pauseQueued = true; });
  }

  addCameraDelta(x: number, y: number): void { this.cameraX += x; this.cameraY += y; }

  readFrame(): InputFrame {
    let moveX = (this.keys.has('KeyD') ? 1 : 0) - (this.keys.has('KeyA') ? 1 : 0) + this.joystickX;
    let moveY = (this.keys.has('KeyW') ? 1 : 0) - (this.keys.has('KeyS') ? 1 : 0) + this.joystickY;
    let sprint = this.keys.has('ShiftLeft') || this.keys.has('ShiftRight');
    let gamepadCameraX = 0;
    let gamepadCameraY = 0;
    const gamepad = navigator.getGamepads?.()[0];
    if (gamepad) {
      const deadzone = (value: number) => Math.abs(value) < 0.16 ? 0 : value;
      moveX += deadzone(gamepad.axes[0] ?? 0);
      moveY += -deadzone(gamepad.axes[1] ?? 0);
      gamepadCameraX = deadzone(gamepad.axes[2] ?? 0);
      gamepadCameraY = deadzone(gamepad.axes[3] ?? 0);
      const pressed = gamepad.buttons.map((button) => button.pressed);
      if ((pressed[2] || pressed[0]) && !(this.previousGamepadButtons[2] || this.previousGamepadButtons[0])) this.kickQueued = true;
      if (pressed[9] && !this.previousGamepadButtons[9]) this.pauseQueued = true;
      sprint ||= Boolean(pressed[4] || pressed[10]);
      this.previousGamepadButtons = pressed;
    }
    const length = Math.hypot(moveX, moveY);
    if (length > 1) { moveX /= length; moveY /= length; }
    const frame = {
      moveX, moveY, sprint, kickPressed: this.kickQueued, pausePressed: this.pauseQueued,
      cameraX: this.cameraX + gamepadCameraX * 0.045,
      cameraY: this.cameraY + gamepadCameraY * 0.035,
    };
    this.kickQueued = this.pauseQueued = false;
    this.cameraX = this.cameraY = 0;
    return frame;
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.clear);
  }

  private onKeyDown = (event: KeyboardEvent) => {
    this.keys.add(event.code);
    if (!event.repeat && (event.code === 'Space' || event.code === 'KeyE')) this.kickQueued = true;
    if (!event.repeat && event.code === 'Escape') this.pauseQueued = true;
  };
  private onKeyUp = (event: KeyboardEvent) => { this.keys.delete(event.code); };
  private clear = () => { this.keys.clear(); this.joystickX = this.joystickY = 0; };
}
